import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageSquare, CheckCircle } from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { site } from "../theme";

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await addDoc(collection(db, "iletisimMesajlari"), {
        ad: form.name.trim(),
        email: form.email.trim(),
        konu: form.subject.trim(),
        mesaj: form.message.trim(),
        tarih: serverTimestamp(),
        durum: "yeni",
      });
      setSubmitted(true);
    } catch (err) {
      console.error("İletişim mesajı gönderilemedi:", err);
      setError("Mesaj gönderilemedi, lütfen tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-page pt-24">
      <div className="bg-gradient-to-br from-[#0F172A] to-brand-blue-dark py-16 text-white">
        <div className="w-full px-6 xl:px-16 text-center">
          <div className="text-brand-gold text-sm font-semibold uppercase tracking-wider mb-2">
            Bize Ulaşın
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">İletişim</h1>
          <p className="text-white/70 max-w-xl mx-auto">
            Sorularınız, önerileriniz veya birlikte üretmek istediğiniz
            projeler için kapımız her zaman açık.
          </p>
        </div>
      </div>

      <div className="w-full px-6 xl:px-16 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Info */}
          <div className="space-y-5">
            {[
              {
                icon: <Phone size={20} className="text-brand-bordo" />,
                title: "Telefon",
                content: site.phone,
                href: `tel:${site.phone}`,
              },
              {
                icon: <Mail size={20} className="text-brand-bordo" />,
                title: "E-posta",
                content: site.email,
                href: `mailto:${site.email}`,
              },
              {
                icon: <MapPin size={20} className="text-brand-bordo" />,
                title: "Adres",
                content: site.address,
                href: undefined,
              },
            ].map((item) => (
              <div key={item.title} className="card flex gap-4">
                <div className="w-10 h-10 bg-bg-soft rounded-full flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <div className="font-semibold text-text-primary text-sm mb-0.5">
                    {item.title}
                  </div>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-text-muted text-sm hover:text-brand-bordo transition-colors break-all"
                    >
                      {item.content}
                    </a>
                  ) : (
                    <div className="text-text-muted text-sm">{item.content}</div>
                  )}
                </div>
              </div>
            ))}

            <div className="card bg-brand-blue text-white">
              <MessageSquare size={18} className="mb-2 text-white/70" />
              <p className="text-sm text-white/80 leading-relaxed">
                WhatsApp grubuna katılmak için mesaj atabilirsiniz.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 card">
            {submitted ? (
              <div className="text-center py-12">
                <CheckCircle size={48} className="text-brand-green mx-auto mb-3" />
                <h3 className="font-bold text-text-primary text-xl mb-2">
                  Mesajınız İletildi!
                </h3>
                <p className="text-text-muted text-sm">
                  En kısa sürede sizinle iletişime geçeceğiz.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-bold text-text-primary text-xl mb-6">
                  Mesaj Gönderin
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">
                        Adınız Soyadınız *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                        placeholder="Adınız Soyadınız"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1.5">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">
                      Konu
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                      placeholder="Mesajınızın konusu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">
                      Mesajınız *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      className="w-full border border-border-light rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white resize-none"
                      placeholder="Mesajınızı buraya yazın..."
                    />
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-secondary w-full flex items-center justify-center gap-2 py-3.5 text-base disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Gönderiliyor..." : "Gönder"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
